import React, { Fragment, useRef } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import Checkbox from '@atlaskit/checkbox';
import FeedbackCollector from '@atlaskit/feedback-collector';
import { CheckboxField, Field } from '@atlaskit/form';
import { ErrorMessage } from '@atlaskit/form/Messages';
import TextArea from '@atlaskit/textarea';

import { aupFeedbackCollectorEntrypointId } from '../../common/constants';
import { type FeedbackMetadata } from '../../common/types';
import { messages } from '../../messages';
import { useFeedbackTextAndMetadata } from '../use-feedback-metadata';

export type AiInteractionFeedbackModalProps = {
	aaid: string;
	/**
	 * Whether to allow the inclusion of the feedback metadata in the feedback description
	 */
	allowFeedbackMetadata: boolean;
	/**
	 * Extra JSON data to submit as part of the feedback description
	 */
	feedbackMetadata?: FeedbackMetadata;
	feedbackTitle: MessageDescriptor;
	includeFeedbackCheckboxLabel: MessageDescriptor;
	isOpen: boolean;
	name: string;
	onSubmit: () => void;
	setIsOpen: (isOpen: boolean) => void;
};

/**
 * Feedback Collector modal for reporting feedback about a particular AI interaction.
 *
 * Includes an optional checkbox to include some caller-provided feedback metadata in the feedback description.
 *
 * This is not necessarily meant to be used directly (but it can be). Instead, wrap it in a more semantically meaningful component like HarmfulContentModal.
 */
export const AiInteractionFeedbackModal = ({
	name,
	aaid,
	allowFeedbackMetadata,
	feedbackMetadata,
	isOpen,
	setIsOpen,
	onSubmit,
	includeFeedbackCheckboxLabel,
	feedbackTitle,
}: AiInteractionFeedbackModalProps): React.JSX.Element => {
	const ref: React.MutableRefObject<HTMLElement | undefined> = useRef<HTMLElement>();

	const { formatMessage } = useIntl();

	const {
		feedbackText,
		setFeedbackText,
		includeHistoryFeedbackMetadata,
		setIncludeHistoryFeedbackMetadata,
		feedbackTextWithMetadata,
	} = useFeedbackTextAndMetadata({
		allowFeedbackMetadata,
		feedbackMetadata,
	});

	const onClose = () => setIsOpen(false);

	return (
		<>
			{isOpen && (
				<FeedbackCollector
					customContent={
						<Fragment>
							{allowFeedbackMetadata && (
								<CheckboxField name="includeInput" defaultIsChecked={false}>
									{({ fieldProps }) => (
										<Checkbox
											{...fieldProps}
											label={formatMessage(includeFeedbackCheckboxLabel)}
											isChecked={includeHistoryFeedbackMetadata}
											onChange={(event) => setIncludeHistoryFeedbackMetadata(event.target.checked)}
										/>
									)}
								</CheckboxField>
							)}
							<Field
								label={formatMessage(messages.feedbackTextAreaLabel)}
								name="feedback-collector-description"
								isRequired
							>
								{({ fieldProps, error }: any) => (
									<Fragment>
										<TextArea
											{...fieldProps}
											minimumRows={4}
											value={feedbackText}
											onChange={(e) => setFeedbackText(e.target.value)}
											resize="auto"
										/>
										{error && (
											<ErrorMessage>
												{formatMessage(messages.feedbackTextAreaRequired)}
											</ErrorMessage>
										)}
									</Fragment>
								)}
							</Field>
						</Fragment>
					}
					onClose={onClose}
					onSubmit={onSubmit}
					atlassianAccountId={aaid}
					name={name}
					// AUP feedback flows into a single pre-set feedback collector entrypoint
					entrypointId={aupFeedbackCollectorEntrypointId}
					feedbackTitle={formatMessage(feedbackTitle)}
					// We are hiding the default text fields, so setting these defaults here
					// will cause these values to be submitted with the feedback behind the scenes
					summaryDefaultValue={feedbackText}
					descriptionDefaultValue={feedbackTextWithMetadata}
					showTypeField={false}
					showDefaultTextFields={false}
					cancelButtonLabel={formatMessage(messages.cancelButtonText)}
					submitButtonLabel={formatMessage(messages.submitButtonText)}
					// @ts-ignore
					shouldReturnFocusRef={ref}
				/>
			)}
		</>
	);
};
